import { useState, type FormEvent } from 'react'
import {
  AleloButton,
  AleloInput,
  AleloLayout,
  AleloPasswordInput,
} from '@/components/AleloLayout'
import { AleloCaptcha } from '@/components/AleloCaptcha'
import styles from '@/components/AleloLayout.module.css'
import { RangoAlert, RangoLink } from '@/components/rango'
import { formatCpf, isValidCpfLength } from '@/utils/cpf'
import { executeSafely } from '@/utils/helpers'
import { useLoginPasswordManager } from './hooks/useLoginPasswordManager'
import locales from './locales/pt-BR.json'

/**
 * S2 — Senha após o Identifier First.
 * CPF identificado no topo (com "Alterar"), label "Senha" visível, olho de
 * visibilidade, esqueci minha senha e captcha.
 */
export default function LoginPasswordScreen() {
  const {
    handleLogin,
    handleForgotPassword,
    handleEditIdentifier,
    displayIdentifier,
    showForgot,
    error: sdkError,
    captchaConfig,
    captchaReady,
    onCaptchaToken,
  } = useLoginPasswordManager()

  const [previewCpf, setPreviewCpf] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string>()
  const [fieldError, setFieldError] = useState<'password' | 'cpf'>()
  const [pending, setPending] = useState(false)

  const hasIdentifier = Boolean(displayIdentifier)

  function messageFor(code: string | undefined) {
    switch (code) {
      case 'PASSWORD_REQUIRED':
        return locales.requiredPassword
      case 'USERNAME_MISSING':
        return locales.usernameMissing
      case 'CAPTCHA_REQUIRED':
        return locales.captchaRequired
      default:
        return undefined
    }
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    if (pending) return
    setError(undefined)
    setFieldError(undefined)

    if (!hasIdentifier && !isValidCpfLength(previewCpf)) {
      setFieldError('cpf')
      return
    }
    if (!password) {
      setFieldError('password')
      return
    }

    setPending(true)
    try {
      await executeSafely(() =>
        handleLogin(password, hasIdentifier ? undefined : previewCpf),
      )
    } catch (err) {
      const code = err instanceof Error ? err.message : ''
      if (code === 'USERNAME_MISSING' || code === 'PASSWORD_REQUIRED') {
        // Erro de campo já aparece inline (role=alert) — não duplica no bloco.
        setFieldError(code === 'USERNAME_MISSING' ? 'cpf' : 'password')
      } else {
        setError(
          code === 'CAPTCHA_REQUIRED' ? locales.captchaRequired : locales.loginFailed,
        )
      }
    } finally {
      setPending(false)
    }
  }

  const formMessage = error || (sdkError ? messageFor(sdkError) ?? locales.loginFailed : undefined)

  return (
    <AleloLayout>
      <h1 className={styles.title}>{locales.title}</h1>
      <p className={styles.subtitle}>{locales.subtitle}</p>

      {hasIdentifier ? (
        <div className={styles.identifierRow}>
          <div>
            <p className={styles.identifierLabel}>{locales.identifierLabel}</p>
            <p className={styles.identifierValue}>{displayIdentifier}</p>
          </div>
          <RangoLink onClick={handleEditIdentifier}>
            {locales.editIdentifier}
          </RangoLink>
        </div>
      ) : (
        <AleloInput
          id="cpf"
          required
          name="username"
          label={locales.previewCpfLabel}
          supportText={locales.previewCpfSupport}
          inputMode="numeric"
          autoComplete="username"
          placeholder="000.000.000-00"
          value={previewCpf}
          error={fieldError === 'cpf' ? locales.usernameMissing : undefined}
          onChange={(e) => {
            setPreviewCpf(formatCpf(e.target.value))
            if (error) setError(undefined)
          }}
          maxLength={14}
          disabled={pending}
        />
      )}

      <form className={styles.stack} onSubmit={onSubmit} noValidate>
        <AleloPasswordInput
          id="password"
          required
          name="password"
          label={locales.passwordLabel}
          supportText={locales.passwordSupport}
          autoComplete="current-password"
          placeholder={locales.passwordPlaceholder}
          value={password}
          error={fieldError === 'password' ? locales.requiredPassword : undefined}
          onChange={(e) => {
            setPassword(e.target.value)
            if (error) setError(undefined)
          }}
          disabled={pending}
          showLabel={locales.showPassword}
          hideLabel={locales.hidePassword}
        />

        <AleloCaptcha config={captchaConfig} onToken={onCaptchaToken} />

        {formMessage ? <RangoAlert tone="negative">{formMessage}</RangoAlert> : null}

        <div className={styles.actions}>
          <AleloButton
            type="submit"
            disabled={pending || !captchaReady}
            loading={pending}
          >
            {pending ? locales.submitting : locales.submit}
          </AleloButton>
          {showForgot ? (
            <div className={styles.center}>
              <RangoLink onClick={() => void handleForgotPassword()}>
                {locales.forgotPassword}
              </RangoLink>
            </div>
          ) : null}
        </div>
      </form>
    </AleloLayout>
  )
}
